import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import type { Summary, User } from '../api/types'
import { initials, relativeDays } from '../lib/format'
import { RailSearch } from './RailSearch'
import { ThemePicker } from './Theme'
import { Icon } from './Icon'
import { AddContactModal } from './AddContactModal'
import { NewDealModal } from './NewDealModal'

interface Props { user: User; summary: Summary | null; onLogout: () => void }

/** Expanded desktop navigation; a labeled drawer on smaller screens. */
export function Shell({ user, summary, onLogout }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState<'contact' | 'opportunity' | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const createRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  useEffect(() => { setMenuOpen(false); setSearchOpen(false); setCreateOpen(false) }, [location.pathname])
  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setMenuOpen(false); setSearchOpen(false); setCreateOpen(false) }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        setSearchOpen(true)
        requestAnimationFrame(() => searchRef.current?.querySelector('input')?.focus())
      }
    }
    const outside = (e: MouseEvent) => {
      if (!createRef.current?.contains(e.target as Node)) setCreateOpen(false)
      if (!searchRef.current?.contains(e.target as Node)) setSearchOpen(false)
    }
    document.addEventListener('keydown', close)
    document.addEventListener('mousedown', outside)
    return () => { document.removeEventListener('keydown', close); document.removeEventListener('mousedown', outside) }
  }, [])
  const routeKey = location.pathname.split('/')[1] ?? ''
  const section = ({ '': 'Your daily brief', leads: 'Leads', pipeline: 'Loan pipeline', drips: 'Campaigns', dialer: 'Dialer', mail: 'Mail pieces', goals: 'Your goals', customize: 'Settings' } as Record<string, string>)[routeKey] ?? routeKey.replace(/^./, c => c.toUpperCase())
  const nav = (to: string, icon: string, label: string, count?: number) => <NavLink key={to} to={to} end={to === '/'}
    // Campaigns live on the Workflows page, so /drips lights the same entry.
    aria-current={to === '/workflows' && routeKey === 'drips' ? 'page' : undefined} onClick={() => setMenuOpen(false)} className={({isActive}) => `dock-link${isActive || (to === '/workflows' && routeKey === 'drips') ? ' active' : ''}`} title={label}>
    <span className="dock-icon"><Icon name={icon} size={20} /></span><span className="dock-label">{label}</span>{count ? <span className="dock-count">{count}</span> : null}
  </NavLink>
  return <div className={`shell atelier-shell${menuOpen ? ' menu-open' : ''}`}>
    <aside className="tool-dock" aria-label="Main navigation">
      <NavLink to="/" className="dock-brand" aria-label="Buckingham home"><span className="sidebar-monogram">b<span>•</span></span><span className="sidebar-brand-name">Buckingham<small>Mortgage workspace</small></span></NavLink>
      <nav className="dock-main">
        <span className="dock-section-label">Workspace</span>
        {nav('/', 'space_dashboard', 'Today')}
        {nav('/contacts', 'group', 'My contacts')}
        {nav('/leads', 'format_list_bulleted', 'Leads', summary?.leads.new)}
        {nav('/dialer', 'call', 'Dialer')}
        {user.is_admin ? nav('/pipeline', 'account_balance', 'Loan pipeline') : null}
        {nav('/calendar', 'calendar_month', 'Calendar')}
        {nav('/tasks', 'task_alt', 'Tasks')}
        {nav('/inbox', 'chat_bubble', 'Inbox', summary?.recent_replies)}
        <span className="dock-section-label">Growth</span>
        {nav('/workflows', 'route', 'Workflows')}
        {nav('/mail', 'local_post_office', 'Mail')}
        {nav('/goals', 'flag', 'Goals')}
        {user.is_admin ? nav('/partners', 'handshake', 'Partners') : null}
      </nav>
      <div className="dock-bottom"><ThemePicker />{nav('/customize', 'tune', 'Settings')}<button className="dock-link" onClick={onLogout} aria-label="Sign out"><span className="dock-icon"><Icon name="logout" size={20} /></span><span className="dock-label">Sign out</span></button></div>
    </aside>
    <div className="workspace">
      <header className="atelier-header">
        <button className="iconbtn mobile-menu" aria-label="Toggle navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}><Icon name="menu" /></button>
        <NavLink to="/" className="atelier-wordmark">buckingham<span>®</span></NavLink>
        <span className="header-divider" /><span className="atelier-location">{section}</span>
        <div ref={searchRef} className={`header-search${searchOpen ? ' search-open' : ''}`}>
          <button className="iconbtn mobile-search-toggle" aria-label="Search contacts, partners and loans" aria-expanded={searchOpen} onClick={() => { setSearchOpen(true); requestAnimationFrame(() => searchRef.current?.querySelector('input')?.focus()) }}><Icon name="search" size={19} /></button>
          <div className="header-search-field"><RailSearch /></div>
        </div>
        <div className="header-create" ref={createRef}>
          <button className="btn primary header-create-toggle" aria-label="Create contact or opportunity" aria-haspopup="menu" aria-expanded={createOpen} onClick={() => setCreateOpen(!createOpen)}><Icon name="add" size={16} /><span>New</span></button>
          {createOpen ? <div className="create-menu" role="menu">
            <button role="menuitem" onClick={() => { setCreating('contact'); setCreateOpen(false); setMenuOpen(false) }}><Icon name="person_add" size={18} /><span>Add contact<small>A new lead or relationship</small></span></button>
            <button role="menuitem" onClick={() => { setCreating('opportunity'); setCreateOpen(false); setMenuOpen(false) }}><Icon name="account_balance" size={18} /><span>Create opportunity<small>Start a loan before the LOS</small></span></button>
          </div> : null}
        </div>
        <span className="sync-indicator" title={summary?.last_sync ? `Loan sync: ${relativeDays(summary.last_sync)}` : 'Awaiting loan sync'}><span className={`sync-dot${summary?.last_sync ? ' connected' : ''}`} />{summary?.last_sync ? 'Synced' : 'Not synced'}</span>
        <NavLink to="/customize" className="header-profile" title={`${user.name} · ${user.is_admin ? 'Admin' : 'Loan officer'}`}><span className="userav">{initials(user.name)}</span></NavLink>
      </header>
      <main className="main" id="main-content"><Outlet /></main>
    </div>
    {creating === 'contact' ? <AddContactModal open onClose={() => setCreating(null)} /> : null}
    {creating === 'opportunity' ? <NewDealModal open onClose={() => setCreating(null)} /> : null}
    {menuOpen ? <button className="rail-backdrop" aria-label="Close navigation" onClick={() => setMenuOpen(false)} /> : null}
  </div>
}
